<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="subtitle" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading purchase details...</span>
            </div>

            <div v-else-if="!purchase" class="empty-wrap">
              <p class="empty-title">Purchase not found</p>
              <router-link to="/purchaselist" class="btn-back">Back to Purchases</router-link>
            </div>

            <template v-else>
              <div class="detail-toolbar">
                <div>
                  <h3>{{ purchase.billno || "No Bill" }}</h3>
                  <p>{{ vendorName }} | {{ formatDate(purchase.invoicedate) }}</p>
                </div>
                <div class="toolbar-actions">
                  <span class="payment-status" :class="paymentStatusClass(creditSummary.payment_status)">
                    {{ paymentStatusLabel(creditSummary.payment_status) }}
                  </span>
                  <router-link to="/purchaselist" class="btn-back">Back</router-link>
                </div>
              </div>

              <div class="summary-grid">
                <div class="summary-box"><span>Net Payable</span><strong>Rs. {{ formatNumber(purchase.netamount) }}</strong></div>
                <div class="summary-box"><span>Paid</span><strong>Rs. {{ formatNumber(creditSummary.paid_amount) }}</strong></div>
                <div class="summary-box"><span>Pending</span><strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong></div>
                <div class="summary-box"><span>Payment Mode</span><strong>{{ purchase.paymentmode || "-" }}</strong></div>
              </div>

              <div class="tabs-row">
                <button :class="{ active: activeTab === 'bill' }" @click="activeTab = 'bill'">Bill Details</button>
                <button :class="{ active: activeTab === 'credit' }" @click="activeTab = 'credit'">Credit Details</button>
              </div>

              <div v-if="activeTab === 'bill'" class="tab-panel">
                <div class="info-grid">
                  <div><span>Bill No</span><strong>{{ purchase.billno || "-" }}</strong></div>
                  <div><span>Invoice No</span><strong>{{ purchase.invoiceno || "-" }}</strong></div>
                  <div><span>Invoice Date</span><strong>{{ formatDate(purchase.invoicedate) }}</strong></div>
                  <div><span>Supplier</span><strong>{{ vendorName }}</strong></div>
                  <div><span>Expense Type</span><strong>{{ purchaseExpenseTypeLabel }}</strong></div>
                  <div><span>Expense Amount</span><strong>Rs. {{ formatNumber(purchaseExpenseAmount) }}</strong></div>
                  <div><span>Expense Remarks</span><strong>{{ purchaseExpenseRemarks }}</strong></div>
                  <div><span>Remarks</span><strong>{{ purchase.remarks || "-" }}</strong></div>
                </div>

                <div class="table-responsive detail-table-wrap">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>Free</th>
                        <th>Remaining</th>
                        <th>Price</th>
                        <th>Net PTR</th>
                        <th>Discount</th>
                        <th>Tax %</th>
                        <th>Taxable</th>
                        <th>Tax Amt</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in purchase.items" :key="item.id || index">
                        <td>{{ index + 1 }}</td>
                        <td><strong>{{ itemName(item) }}</strong><small v-if="item.batchno">Batch: {{ item.batchno }}</small></td>
                        <td>{{ formatQty(item.quantity) }}</td>
                        <td>{{ formatQty(item.freequantity) }}</td>
                        <td>{{ formatQty(item.remainingqty) }}</td>
                        <td>Rs. {{ formatNumber(item.purchaseprice) }}</td>
                        <td>Rs. {{ formatNumber(netPurchaseRate(item)) }}</td>
                        <td>{{ formatNumber(item.discount) }}</td>
                        <td>{{ item.taxpercent || 0 }}</td>
                        <td>Rs. {{ formatNumber(item.taxablevalue) }}</td>
                        <td>Rs. {{ formatNumber(item.taxamount) }}</td>
                        <td>Rs. {{ formatNumber(rowTotal(item)) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr><td colspan="11">Total Amount</td><td>Rs. {{ formatNumber(purchase.totalamount) }}</td></tr>
                      <tr><td colspan="11">Discount</td><td>- Rs. {{ formatNumber(purchase.discountamount) }}</td></tr>
                      <tr v-if="purchaseExpenseAmount"><td colspan="11">{{ purchaseExpenseTypeLabel }}</td><td>Rs. {{ formatNumber(purchaseExpenseAmount) }}</td></tr>
                      <tr><td colspan="11">Tax Amount</td><td>Rs. {{ formatNumber(purchase.taxamount) }}</td></tr>
                      <tr><td colspan="11">Round Off</td><td>Rs. {{ formatNumber(purchase.roundoff) }}</td></tr>
                      <tr class="net-row"><td colspan="11">Net Payable</td><td>Rs. {{ formatNumber(purchase.netamount) }}</td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div v-else class="tab-panel">
                <div class="credit-actions">
                  <button class="btn-add-payment" :disabled="creditSummary.outstanding_balance <= 0" @click="openPaymentModal">
                    Add Payment
                  </button>
                </div>

                <div class="credit-grid">
                  <div><span>Credit Purchase</span><strong>{{ purchase.is_credit ? "Yes" : "No" }}</strong></div>
                  <div><span>Due Date</span><strong>{{ formatDate(creditSummary.due_date || purchase.due_date) }}</strong></div>
                  <div><span>Initial Paid</span><strong>Rs. {{ formatNumber(purchase.paid_amount) }}</strong></div>
                  <div><span>Payment Paid</span><strong>Rs. {{ formatNumber(creditSummary.payment_paid_amount) }}</strong></div>
                  <div><span>Total Paid</span><strong>Rs. {{ formatNumber(creditSummary.paid_amount) }}</strong></div>
                  <div><span>Pending Balance</span><strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong></div>
                </div>

                <div class="section-title-row">
                  <h4>Payments Made</h4>
                  <span>{{ payments.length }} payment{{ payments.length === 1 ? "" : "s" }}</span>
                </div>

                <div v-if="!payments.length" class="empty-receipts">No payments recorded for this purchase bill.</div>
                <div v-else class="table-responsive detail-table-wrap">
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th>Payment No</th>
                        <th>Date</th>
                        <th>Mode</th>
                        <th>Reference</th>
                        <th>Remarks</th>
                        <th class="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payment in payments" :key="payment.paymentid">
                        <td>{{ payment.billno || payment.paymentid }}</td>
                        <td>{{ formatDate(payment.payment_date) }}</td>
                        <td>{{ payment.payment_mode || "-" }}</td>
                        <td>{{ payment.reference_no || "-" }}</td>
                        <td>{{ payment.remarks || "-" }}</td>
                        <td class="text-end">Rs. {{ formatNumber(payment.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPaymentModal" class="modal-backdrop-custom">
      <div class="payment-modal">
        <div class="modal-head">
          <h4>Add Payment</h4>
          <button class="modal-close" :disabled="paymentSubmitting" @click="closePaymentModal">x</button>
        </div>
        <div class="modal-body-custom">
          <div class="payment-balance">
            <span>Pending</span>
            <strong>Rs. {{ formatNumber(creditSummary.outstanding_balance) }}</strong>
          </div>
          <div class="form-group-custom">
            <label>Amount</label>
            <input v-model.number="paymentForm.amount" type="number" min="0" :max="creditSummary.outstanding_balance" step="0.01" class="form-control" />
          </div>
          <div class="form-group-custom">
            <label>Payment Date</label>
            <input v-model="paymentForm.payment_date" type="date" class="form-control" />
          </div>
          <div class="form-group-custom">
            <label>Payment Mode</label>
            <select v-model="paymentForm.payment_mode" class="form-control">
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CHEQUE">Cheque</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div class="form-group-custom">
            <label>Remarks</label>
            <textarea v-model="paymentForm.remarks" class="form-control" rows="2"></textarea>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel-payment" :disabled="paymentSubmitting" @click="closePaymentModal">Cancel</button>
          <button class="btn-save-payment" :disabled="paymentSubmitting" @click="submitPayment">
            {{ paymentSubmitting ? "Saving..." : "Save Payment" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

export default {
  name: "purchase-details",
  data() {
    return {
      title: "Purchase Details",
      activeTab: "bill",
      loading: false,
      itemCatalog: {},
      vendorCatalog: {},
      showPaymentModal: false,
      paymentSubmitting: false,
      paymentForm: { amount: 0, payment_date: "", payment_mode: "CASH", remarks: "" },
      purchase: null,
      purchaseid: this.$route.query.id || "",
    };
  },
  computed: {
    subtitle() {
      return this.purchase?.billno ? `Bill ${this.purchase.billno}` : "View bill and payment details";
    },
    payments() {
      return this.purchase?.payments || [];
    },
    vendorName() {
      return this.vendorCatalog[this.purchase?.vendor] || this.purchase?.vendor || "-";
    },
    creditSummary() {
      const netamount = Number(this.purchase?.netamount || 0);
      const paid = Number(this.purchase?.paid_amount || 0);
      return {
        netamount,
        paid_amount: paid,
        payment_paid_amount: 0,
        outstanding_balance: Math.max(netamount - paid, 0),
        payment_status: this.purchase?.payment_status || "PAID",
        is_credit: Boolean(this.purchase?.is_credit),
        due_date: this.purchase?.due_date || null,
        ...(this.purchase?.credit_summary || {}),
      };
    },
    purchaseExpense() {
      return this.purchase?.purchase_expense || this.purchase?.purchase_expenses?.[0] || null;
    },
    purchaseExpenseAmount() {
      return Number(this.purchaseExpense?.amount ?? this.purchase?.miscellaneous_amount ?? 0);
    },
    purchaseExpenseTypeLabel() {
      const type = this.purchaseExpense?.expense_type;
      if (!type && !this.purchaseExpenseAmount) return "-";
      return this.formatExpenseType(type || "MISCELLANEOUS");
    },
    purchaseExpenseRemarks() {
      return this.purchaseExpense?.remarks || "-";
    },
  },
  beforeMount() {
    this.fetchPurchase();
  },
  methods: {
    async fetchPurchase() {
      if (!this.purchaseid) {
        Swal.fire({ icon: "warning", title: "Missing Purchase", text: "Purchase id is required." });
        return;
      }
      this.loading = true;
      try {
        const response = await axios.get(`/v1/purchases/${this.purchaseid}`);
        this.purchase = response.data;
        await Promise.all([this.hydrateItemNames(), this.hydrateVendorName()]);
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load purchase details." });
      } finally {
        this.loading = false;
      }
    },
    async hydrateItemNames() {
      const missingIds = (this.purchase?.items || [])
        .filter((item) => item.itemid && !item.itemname && !this.itemCatalog[item.itemid])
        .map((item) => item.itemid);
      await Promise.all([...new Set(missingIds)].map(async (itemid) => {
        try {
          const response = await axios.get(`/v1/items/${itemid}`);
          if (response.data?.itemid) this.itemCatalog[itemid] = response.data.itemname;
        } catch (error) {
          console.error(error);
        }
      }));
    },
    async hydrateVendorName() {
      const vendorid = this.purchase?.vendor;
      if (!vendorid || this.vendorCatalog[vendorid]) return;
      try {
        const response = await axios.get(`/v1/vendors/${vendorid}`);
        this.vendorCatalog[vendorid] = response.data?.vendorname || vendorid;
      } catch (error) {
        console.error(error);
      }
    },
    itemName(item) {
      return item.itemname || this.itemCatalog[item.itemid] || item.itemid || "-";
    },
    todayString() {
      const date = new Date();
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    openPaymentModal() {
      const outstanding = Number(this.creditSummary.outstanding_balance || 0);
      if (outstanding <= 0) return;
      this.paymentForm = { amount: outstanding, payment_date: this.todayString(), payment_mode: "CASH", remarks: "" };
      this.showPaymentModal = true;
    },
    closePaymentModal() {
      if (this.paymentSubmitting) return;
      this.showPaymentModal = false;
    },
    async submitPayment() {
      const amount = Number(this.paymentForm.amount || 0);
      const outstanding = Number(this.creditSummary.outstanding_balance || 0);
      if (amount <= 0) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Payment amount must be greater than zero." });
        return;
      }
      if (amount > outstanding) {
        Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Payment cannot exceed pending balance." });
        return;
      }
      this.paymentSubmitting = true;
      try {
        await axios.post(`/v1/purchases/${this.purchaseid}/payments`, {
          amount,
          payment_date: this.paymentForm.payment_date || undefined,
          payment_mode: this.paymentForm.payment_mode,
          remarks: this.paymentForm.remarks || undefined,
        });
        this.showPaymentModal = false;
        await this.fetchPurchase();
        Swal.fire({ icon: "success", title: "Payment Added", timer: 1400, showConfirmButton: false });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Payment Failed", text: error.response?.data?.detail || "Could not add payment." });
      } finally {
        this.paymentSubmitting = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date)) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    rowTotal(item) {
      const qty = Number(item.quantity || 0);
      const price = Number(item.purchaseprice || 0);
      const discount = Number(item.discount || 0);
      const lineGross = qty * price * (1 - discount / 100);
      return item.istaxinclusive ? lineGross : lineGross + Number(item.taxamount || 0);
    },
    linePurchaseBase(item) {
      return Number(item.quantity || 0) * Number(item.purchaseprice || 0);
    },
    lineStockQty(item) {
      return Number(item.quantity || 0) + Number(item.freequantity || 0);
    },
    netPurchaseRate(item) {
      if (item.netpurchaserate !== null && item.netpurchaserate !== undefined) {
        return Number(item.netpurchaserate || 0);
      }
      const ptr = Number(item.purchaseprice || 0);
      const stockQty = this.lineStockQty(item);
      const items = this.purchase?.items || [];
      const totalBase = items.reduce((sum, row) => sum + this.linePurchaseBase(row), 0);
      const otherCost = this.purchaseExpenseAmount;
      const lineBase = this.linePurchaseBase(item);
      if (!stockQty || !totalBase || !otherCost) return ptr;
      const lineOtherCost = otherCost * (lineBase / totalBase);
      return Number(((lineBase + lineOtherCost) / stockQty).toFixed(2));
    },
    formatExpenseType(type) {
      return String(type || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    paymentStatusLabel(status) {
      const normalized = (status || "PAID").toUpperCase();
      if (normalized === "PARTIAL") return "Partial";
      if (normalized === "UNPAID") return "Unpaid";
      return "Paid";
    },
    paymentStatusClass(status) {
      const normalized = (status || "PAID").toUpperCase();
      return {
        "payment-paid": normalized === "PAID",
        "payment-partial": normalized === "PARTIAL",
        "payment-unpaid": normalized === "UNPAID",
      };
    },
  },
};
</script>

<style scoped>
.loading-wrap, .empty-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; min-height:260px; color:#6b7280; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-title { margin:0; font-weight:700; color:#111827; }
.detail-toolbar { display:flex; justify-content:space-between; gap:16px; align-items:flex-start; margin-bottom:16px; }
.detail-toolbar h3 { margin:0 0 4px; font-size:1.15rem; font-weight:800; color:#111827; }
.detail-toolbar p { margin:0; color:#6b7280; font-size:.86rem; }
.toolbar-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
.btn-back { display:inline-flex; align-items:center; height:34px; padding:0 14px; border-radius:6px; border:1px solid #e5e7eb; background:#f9fafb; color:#374151; font-weight:700; font-size:.82rem; text-decoration:none; }
.payment-status { display:inline-flex; align-items:center; height:28px; border-radius:20px; padding:0 11px; font-size:.76rem; font-weight:800; text-transform:uppercase; }
.payment-paid { background:#dcfce7; color:#166534; border:1px solid #86efac; }
.payment-partial { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; }
.payment-unpaid { background:#fee2e2; color:#b91c1c; border:1px solid #fca5a5; }
.summary-grid, .info-grid, .credit-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(180px, 1fr)); gap:10px; margin-bottom:16px; }
.summary-box, .info-grid > div, .credit-grid > div { border:1px solid #e5e7eb; border-radius:8px; background:#f9fafb; padding:12px; }
.summary-box span, .info-grid span, .credit-grid span { display:block; margin-bottom:4px; color:#6b7280; font-size:.72rem; font-weight:800; text-transform:uppercase; }
.summary-box strong, .info-grid strong, .credit-grid strong { color:#111827; font-size:.92rem; overflow-wrap:anywhere; }
.tabs-row { display:flex; gap:6px; border-bottom:1px solid #e5e7eb; margin-bottom:14px; }
.tabs-row button { border:none; background:transparent; padding:9px 12px; color:#6b7280; font-weight:800; border-bottom:2px solid transparent; }
.tabs-row button.active { color:#ea580c; border-bottom-color:#ea580c; }
.tab-panel { min-height:240px; }
.detail-table-wrap { border:1px solid #e5e7eb; border-radius:8px; overflow:auto; }
.detail-table { width:100%; border-collapse:collapse; min-width:1040px; background:#fff; }
.detail-table th, .detail-table td { padding:9px 10px; border-bottom:1px solid #f1f5f9; font-size:.82rem; text-align:left; vertical-align:top; }
.detail-table th { background:#f9fafb; color:#6b7280; font-size:.72rem; font-weight:800; text-transform:uppercase; }
.detail-table small { display:block; color:#6b7280; margin-top:2px; }
.detail-table tfoot td { background:#fff7f0; font-weight:800; text-align:right; }
.detail-table .net-row td { background:#ffedd5; color:#c2410c; }
.text-end { text-align:right !important; }
.section-title-row { display:flex; justify-content:space-between; align-items:center; gap:12px; margin:16px 0 10px; }
.section-title-row h4 { margin:0; font-size:1rem; font-weight:800; color:#111827; }
.section-title-row span { color:#6b7280; font-size:.82rem; font-weight:700; }
.empty-receipts { border:1px dashed #d1d5db; border-radius:8px; padding:22px; color:#6b7280; text-align:center; background:#f9fafb; }
.credit-actions { display:flex; justify-content:flex-end; margin-bottom:12px; }
.btn-add-payment { height:34px; padding:0 14px; border:none; border-radius:6px; background:#ea580c; color:#fff; font-size:.82rem; font-weight:800; }
.btn-add-payment:disabled { background:#d1d5db; cursor:not-allowed; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; padding:16px; background:rgba(17,24,39,.45); }
.payment-modal { width:min(440px, 100%); border-radius:8px; background:#fff; box-shadow:0 18px 45px rgba(15,23,42,.25); overflow:hidden; }
.modal-head { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-bottom:1px solid #e5e7eb; }
.modal-head h4 { margin:0; font-size:1rem; font-weight:800; color:#111827; }
.modal-close { width:28px; height:28px; border:none; border-radius:50%; background:#f3f4f6; color:#374151; font-weight:800; }
.modal-body-custom { padding:16px; }
.payment-balance { display:flex; justify-content:space-between; gap:12px; margin-bottom:14px; padding:10px 12px; border:1px solid #fed7aa; border-radius:8px; background:#fff7f0; }
.payment-balance span { color:#9a3412; font-size:.78rem; font-weight:800; text-transform:uppercase; }
.payment-balance strong { color:#c2410c; }
.form-group-custom { margin-bottom:12px; }
.form-group-custom label { display:block; margin-bottom:5px; color:#6b7280; font-size:.76rem; font-weight:800; }
.form-group-custom .form-control { min-height:36px; border:1px solid #e5e7eb; border-radius:6px; font-size:.86rem; }
.modal-actions { display:flex; justify-content:flex-end; gap:8px; padding:12px 16px; border-top:1px solid #e5e7eb; background:#f9fafb; }
.btn-cancel-payment, .btn-save-payment { height:34px; padding:0 14px; border-radius:6px; font-size:.82rem; font-weight:800; }
.btn-cancel-payment { border:1px solid #e5e7eb; background:#fff; color:#374151; }
.btn-save-payment { border:none; background:#ea580c; color:#fff; }
.btn-save-payment:disabled, .btn-cancel-payment:disabled { opacity:.7; cursor:not-allowed; }
@media (max-width:575px) {
  .detail-toolbar { flex-direction:column; }
  .toolbar-actions, .credit-actions { justify-content:flex-start; }
}
</style>
